// Event row shape — mirrors the Supabase schema in supabase/migrations/0001_init.sql.
// All fields the schema allows are typed; callers pass only the ones relevant to the
// event type. Keep this file pure: no React, no Supabase imports.

export type EventType =
  | "feed"
  | "nurse"
  | "pump"
  | "sleep"
  | "diaper"
  | "fuss"
  | "cue"
  | "milestone"
  | "weight"
  | "note";

export type EventRow = {
  id: string;
  baby_id: string;
  type: EventType;
  at: string; // ISO
  end_at: string | null;
  amount_oz: number | null;
  duration_min: number | null;
  side: "L" | "R" | "both" | null;
  sleep_kind: "nap" | "night" | null;
  diaper_kind: "wet" | "dirty" | "both" | null;
  cue_tier: 1 | 2 | 3 | null;
  milestone_key: string | null;
  weight_grams: number | null;
  note: string | null;
  caretaker_label: string | null;
  user_id: string | null;
  source: "manual" | "nl-parsed" | "realtime";
  created_at: string;
};

export const EVENT_COLUMNS =
  "id, baby_id, type, at, end_at, amount_oz, duration_min, side, sleep_kind, diaper_kind, cue_tier, milestone_key, weight_grams, note, caretaker_label, user_id, source, created_at";

// --- Selectors over a sorted (newest-first) event list ------------------------------------

export function currentSleep(events: EventRow[]): EventRow | null {
  return events.find((e) => e.type === "sleep" && e.end_at === null) ?? null;
}

export function lastFeed(events: EventRow[]): EventRow | null {
  return events.find((e) => e.type === "feed" || e.type === "nurse") ?? null;
}

export function lastFeedOz(events: EventRow[], fallback = 2): number {
  const recent = events.find((e) => e.type === "feed" && e.amount_oz != null);
  return recent?.amount_oz ?? fallback;
}

// A quick "Nursing" tap logs an amount, so nursing counts toward the day's
// milk like a bottle does. Chain: the most recent nurse's amount, or a legacy
// duration-based nurse's estimate, or 3 oz — the assumed intake for one full
// session (~20 min at nursingOzEstimate's rate).
export function lastNurseOz(events: EventRow[], fallback = 3): number {
  for (const e of events) {
    if (e.type !== "nurse") continue;
    if (e.amount_oz != null) return e.amount_oz;
    if (e.duration_min != null) return nursingOzEstimate(e.duration_min);
  }
  return fallback;
}

export function lastWeight(events: EventRow[]): EventRow | null {
  return events.find((e) => e.type === "weight") ?? null;
}

// A newborn transfers roughly 0.5–1 oz per 5 minutes of active nursing — so a
// 15–20 min session is about 2–3 oz. We use the midpoint (~0.15 oz/min) as a
// rough estimate so nursing counts toward the day's milk total. It's only an
// estimate: transfer efficiency and supply vary a lot baby to baby.
export function nursingOzEstimate(durationMin: number): number {
  return Math.round(durationMin * 0.15 * 10) / 10;
}

// --- Today's totals --------------------------------------------------------------------------

export type DayTotals = {
  feeds: number;
  oz: number;
  sleepMinutes: number;
  diapers: { wet: number; dirty: number };
};

export function startOfLocalDay(now: Date = new Date()): Date {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Sleep is the one event with real duration, so it's the one that can straddle
// midnight: a night sleep started at 11:40pm belongs to *both* days. Every
// day-scoped view treats a sleep as the interval [at, end_at) and asks whether
// it overlaps the day — never "did it start today", which loses the whole
// night sleep from the morning's view.

/** End of a sleep in ms: its logged end, or `now` while it's still running. */
export function sleepEndMs(e: EventRow, now: Date = new Date()): number {
  return e.end_at ? new Date(e.end_at).getTime() : now.getTime();
}

/** True when a sleep overlaps [windowStart, windowEnd) by any amount. */
export function sleepOverlapsWindow(
  e: EventRow,
  windowStart: number,
  windowEnd: number,
  now: Date = new Date(),
): boolean {
  return new Date(e.at).getTime() < windowEnd && sleepEndMs(e, now) > windowStart;
}

/** Minutes of a sleep that fall inside [windowStart, windowEnd) — clipped at both edges. */
export function sleepMinutesInWindow(
  e: EventRow,
  windowStart: number,
  windowEnd: number,
  now: Date = new Date(),
): number {
  const start = Math.max(new Date(e.at).getTime(), windowStart);
  const end = Math.min(sleepEndMs(e, now), windowEnd);
  return Math.max(0, Math.round((end - start) / 60_000));
}

export function todayTotals(events: EventRow[], now: Date = new Date()): DayTotals {
  const dayStart = startOfLocalDay(now).getTime();
  const dayEnd = dayStart + 86_400_000;
  const totals: DayTotals = { feeds: 0, oz: 0, sleepMinutes: 0, diapers: { wet: 0, dirty: 0 } };

  for (const e of events) {
    const t = new Date(e.at).getTime();
    // Sleep is scoped by overlap below; everything else by its single timestamp.
    if (e.type !== "sleep" && t < dayStart) continue;

    if (e.type === "feed") {
      totals.feeds++;
      if (e.amount_oz != null) totals.oz += e.amount_oz;
    } else if (e.type === "nurse") {
      totals.feeds++;
      if (e.amount_oz != null) totals.oz += e.amount_oz;
      else if (e.duration_min != null) totals.oz += nursingOzEstimate(e.duration_min);
    } else if (e.type === "sleep") {
      // Counts the slice inside today, so an 11:40pm→6am night sleep adds its
      // 6 hours to *this* morning and its 20 minutes to last night.
      totals.sleepMinutes += sleepMinutesInWindow(e, dayStart, dayEnd, now);
    } else if (e.type === "diaper") {
      if (e.diaper_kind === "wet" || e.diaper_kind === "both") totals.diapers.wet++;
      if (e.diaper_kind === "dirty" || e.diaper_kind === "both") totals.diapers.dirty++;
    }
  }

  return totals;
}

// --- Time formatting ------------------------------------------------------------------------

export function formatRelative(ts: string, now: Date = new Date()): string {
  const ms = now.getTime() - new Date(ts).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  const rem = min % 60;
  if (hours < 24) return rem ? `${hours}h ${rem}m ago` : `${hours}h ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export function formatClock(ts: string): string {
  return new Date(ts).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

// --- Weight unit conversion -----------------------------------------------------------------

export function gramsToLbOz(grams: number): { lb: number; oz: number } {
  // Round to whole ounces first, then split — otherwise 15.99 oz rounds to 16
  // and shows as "6 lb 16 oz" instead of carrying into "7 lb 0 oz".
  const totalOz = Math.round(grams / 28.3495);
  const lb = Math.floor(totalOz / 16);
  const oz = totalOz - lb * 16;
  return { lb, oz };
}

export function lbOzToGrams(lb: number, oz: number): number {
  return Math.round((lb * 16 + oz) * 28.3495);
}
